// ---------------------------------------------------------------------------
// template-description-copy.ts — localizing a sentence this app does not own.
//
// `packages/cogo-schema/template-catalog.json` gives every Starter one English
// line, e.g.
//   "4-page ecommerce template with 7 blocks featuring navigation, hero
//    section, forms, footer"
// and that catalog is a shared package with consumers beyond this app, so its
// text is DATA. The Starters gallery still has to show it in five languages.
//
// The choice made here is to PARSE THE CATALOG'S OWN CLAIM and re-render it,
// rather than to re-derive a description from the template's `blocks` array.
// Re-deriving was tried first and is why this comment exists: a predicate over
// block ids reproduces 46 of the catalog's 47 sentences and gets
// `ecommerce-storefront-new3` wrong (it calls `shop-checkout-stepper` a form
// and the predicate did not), which means a re-derived Korean card would have
// claimed something the English card did not. Parsing cannot do that: every
// word in the localized line came from the catalog's own sentence.
//
// The English table is the IDENTITY of this transform — its vocabulary maps
// each catalog word to itself and its `describe` rebuilds the same grammar —
// so `localizeTemplateDescription(d, EN.catalog) === d` for every entry, and
// the English surface is untouched by construction rather than by a branch.
// `__tests__/template-description-copy.test.ts` measures exactly that, plus
// that all 47 entries parse and that no locale is missing a word.
// ---------------------------------------------------------------------------

import type { StartersCopy } from '@/i18n/studio-starters-copy'

type CatalogCopy = StartersCopy['catalog']

/**
 * The grammar every catalog description is written in. Anchored at both ends:
 * a sentence that is only PARTLY of this shape must not be half-translated.
 */
const DESCRIPTION_GRAMMAR =
  /^(?:Single-page|(\d+)-page) ([a-z]+) template with (\d+) blocks featuring (.+)$/

export interface ParsedTemplateDescription {
  pages: number
  domain: string
  blocks: number
  features: string[]
}

/** `null` when the sentence is not of the catalog's shape. */
export function parseTemplateDescription(
  description: string,
): ParsedTemplateDescription | null {
  const match = DESCRIPTION_GRAMMAR.exec(description.trim())
  if (!match) return null
  const [, pageCount, domain, blockCount, featureList] = match
  const features = featureList.split(', ').filter(Boolean)
  if (features.length === 0) return null
  return {
    // "Single-page" carries no digits — the capture is absent, not "1".
    pages: pageCount ? Number(pageCount) : 1,
    domain,
    blocks: Number(blockCount),
    features,
  }
}

/**
 * The catalog's own sentence, said in the active locale.
 *
 * Falls back to the English string whenever the sentence does not parse — a
 * template whose description was hand-written rather than generated keeps its
 * words rather than losing them. Unknown domains and clause terms fall back to
 * themselves for the same reason; the test pins that today nothing does.
 */
export function localizeTemplateDescription(
  description: string,
  copy: CatalogCopy,
): string {
  const parsed = parseTemplateDescription(description)
  if (!parsed) return description
  // The field names are compound on purpose — see `studio-starters-copy.ts`.
  return copy.describe({
    pageCount: parsed.pages,
    blockCount: parsed.blocks,
    domainLabel: copy.domains[parsed.domain] ?? parsed.domain,
    featureList: parsed.features
      .map((feature) => copy.features[feature] ?? feature)
      .join(copy.featureSeparator),
  })
}

/** A catalog `domain` value said in the active locale. */
export function localizeTemplateDomain(domain: string, copy: CatalogCopy): string {
  return copy.domains[domain] ?? domain
}

/** A catalog `tags` value said in the active locale. A tag that is also a
 *  domain (`agency`, `content`, …) is looked up in BOTH tables so the chip row
 *  and the filter row cannot disagree about the same word. */
export function localizeTemplateTag(tag: string, copy: CatalogCopy): string {
  return copy.tags[tag] ?? copy.domains[tag] ?? tag
}
